import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowRightIcon,
  ChevronDownIcon,
  EyeOffIcon,
  PlusIcon,
  TagIcon,
  TagsIcon,
  XIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useCreateLabel, useReplaceTaskLabels } from '@/features/labels';
import { workspaceKeys } from '@/features/workspaces';
import type { LabelView, TaskView } from '@/lib/api/generated/model';
import type { BoardStepSection } from '../boardModel';
import {
  declaredTargets,
  reverseTransitionExists,
  runBulk,
  summariseBulk,
  type BulkOutcome,
} from '../boardPower';
import { useBoardSelectionStore } from '../boardSelectionStore';
import type { MoveRefusal } from '../errors';
import { useMoveTask } from '../hooks/useMoveTask';
import { UNDO_WINDOW_MS } from '../hooks/useUndoableMove';
import { useSetTaskHidden } from '../hooks/useTaskLifecycle';

/** The refusal, in the two or three words a summary has room for. */
const REFUSAL_WORD: Record<MoveRefusal['kind'], string> = {
  busy: 'busy',
  stale: 'out of date',
  illegal: 'not allowed from here',
  'missing-profile': 'no AI profile',
  'needs-account': 'needs your account',
  unknown: 'failed',
};

export interface BoardSelectionBarProps {
  workspaceId: string;
  steps: readonly BoardStepSection[];
  tasks: readonly TaskView[];
  labelRegistry: readonly LabelView[];
}

/**
 * What can be done to several selected cards at once (spec 098 FR-007 –
 * FR-012).
 *
 * Every action runs per card, in order, with that card's own version, and ends
 * in one sentence: how many it applied to, how many were refused and why. A
 * refused card stays where and how it was and stays selected, so the reader
 * can see which ones and try again; the others leave the selection. "Move
 * to…" offers exactly the steps the selection's column declares — the same
 * rule the service will apply — and nothing else.
 */
export function BoardSelectionBar({
  workspaceId,
  steps,
  tasks,
  labelRegistry,
}: BoardSelectionBarProps) {
  const selection = useBoardSelectionStore();
  const queryClient = useQueryClient();
  const moveTask = useMoveTask();
  const replaceLabels = useReplaceTaskLabels();
  const createLabel = useCreateLabel();
  const setHidden = useSetTaskHidden();
  /*
    The name being typed for a label that does not exist yet, or null when
    nothing is. A workspace starts with no labels at all, and a bar whose
    "Add label" is simply disabled then explains nothing and leads nowhere; the
    first label gets made right here, and lands on the selection in the same
    breath.
  */
  const [newLabel, setNewLabel] = useState<string | null>(null);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  if (selection.workspaceId !== workspaceId || selection.taskIds.length === 0) {
    return null;
  }
  const stepId = selection.stepId ?? '';
  const targets = declaredTargets(steps, stepId);
  const count = selection.taskIds.length;
  const taskOf = (id: string) => tasks.find((task) => task.id === id);
  // Only what the selection actually carries can be taken off it.
  const labelsOnSelection = labelRegistry.filter((label) =>
    selection.taskIds.some((id) =>
      taskOf(id)?.labels?.some((carried) => carried.id === label.id),
    ),
  );

  async function run(
    verb: string,
    act: (task: TaskView) => Promise<BulkOutcome>,
    /**
     * The way back, when there is one (spec 125 FR-010, SC-004): offered in
     * the summary toast for as long as a single move's Undo is, and run only
     * over the cards this batch actually changed.
     */
    undo?: () => void,
  ) {
    const ids = [...selection.taskIds];
    setProgress({ done: 0, total: ids.length });
    const outcomes = await runBulk(
      ids,
      async (id) => {
        const task = taskOf(id);
        if (!task) return { id, ok: false, reason: 'gone' };
        try {
          return await act(task);
        } catch {
          return { id, ok: false, reason: 'failed' };
        }
      },
      (done, total) => {
        setProgress({ done, total });
      },
    );
    setProgress(null);
    const summary = summariseBulk(outcomes, verb);
    const changed = outcomes.some((o) => o.ok && o.changed);
    const options =
      undo && changed
        ? {
            duration: UNDO_WINDOW_MS,
            action: { label: 'Undo', onClick: undo },
          }
        : undefined;
    if (outcomes.some((o) => !o.ok)) toast.warning(summary, options);
    else toast.success(summary, options);
    selection.remove(outcomes.filter((o) => o.ok).map((o) => o.id));
    // One refresh at the end, whatever the mutations did on their own: the last
    // card may have been refused, and a refusal rolls its own optimistic write
    // back without asking the server what the others left behind.
    void queryClient.invalidateQueries({
      queryKey: workspaceKeys.detail(workspaceId),
    });
  }

  const moveAll = (targetStepId: string) => {
    // Every card in a selection stands in one column, so one rule says whether
    // the whole batch can go back — the same declared-way-back rule a single
    // move's Undo follows (spec 098 FR-002).
    const from = stepId;
    const moved: { taskId: string; version: number }[] = [];
    const canGoBack = reverseTransitionExists(steps, from, targetStepId);
    return run(
      'moved',
      async (task) => {
        const result = await moveTask.mutateAsync({
          workspaceId,
          taskId: task.id,
          targetStepId,
          expectedVersion: task.version,
        });
        if (result.refusal) {
          return {
            id: task.id,
            ok: false,
            reason: REFUSAL_WORD[result.refusal.kind],
          };
        }
        moved.push({ taskId: task.id, version: result.task.version });
        return { id: task.id, ok: true, changed: true };
      },
      canGoBack
        ? () => {
            void undoMoves(moved, from);
          }
        : undefined,
    );
  };

  /** Each card the batch moved, back to where it was, with the reason "Undo". */
  async function undoMoves(
    moved: readonly { taskId: string; version: number }[],
    from: string,
  ) {
    const outcomes = await runBulk(
      moved.map((m) => m.taskId),
      async (id) => {
        const version = moved.find((m) => m.taskId === id)?.version ?? 0;
        try {
          const result = await moveTask.mutateAsync({
            workspaceId,
            taskId: id,
            targetStepId: from,
            expectedVersion: version,
            reason: 'Undo',
          });
          return result.refusal
            ? { id, ok: false, reason: REFUSAL_WORD[result.refusal.kind] }
            : { id, ok: true, changed: true };
        } catch {
          return { id, ok: false, reason: 'failed' };
        }
      },
    );
    const summary = summariseBulk(outcomes, 'moved back');
    if (outcomes.some((o) => !o.ok)) toast.warning(summary);
    else toast.success(summary);
    void queryClient.invalidateQueries({
      queryKey: workspaceKeys.detail(workspaceId),
    });
  }

  const relabelAll = (label: LabelView, add: boolean) =>
    run(add ? 'labelled' : 'unlabelled', async (task) => {
      const current = task.labels ?? [];
      const has = current.some((l) => l.id === label.id);
      if (has === add) return { id: task.id, ok: true, changed: false };
      const next = add
        ? [
            ...current,
            {
              id: label.id,
              name: label.name,
              color: label.color,
              actor_type: 'user' as const,
            },
          ]
        : current.filter((l) => l.id !== label.id);
      await replaceLabels.mutateAsync({
        workspaceId,
        taskId: task.id,
        labelIds: next.map((l) => l.id),
        optimistic: next,
      });
      return { id: task.id, ok: true, changed: true };
    });

  const hideAll = () => {
    const hidden: string[] = [];
    return run(
      'hidden',
      async (task) => {
        if (task.hidden_at != null)
          return { id: task.id, ok: true, changed: false };
        await setHidden.mutateAsync({
          workspaceId,
          taskId: task.id,
          hidden: true,
        });
        hidden.push(task.id);
        return { id: task.id, ok: true, changed: true };
      },
      // Hiding is always reversible, so a batch of it always offers the way
      // back, like one hide does (spec 125 SC-004).
      () => {
        void runBulk(hidden, async (id) => {
          try {
            await setHidden.mutateAsync({
              workspaceId,
              taskId: id,
              hidden: false,
            });
            return { id, ok: true, changed: true };
          } catch {
            return { id, ok: false, reason: 'failed' };
          }
        }).then((outcomes) => {
          const summary = summariseBulk(outcomes, 'back on the board');
          if (outcomes.some((o) => !o.ok)) toast.warning(summary);
          else toast.success(summary);
        });
      },
    );
  };

  async function createAndAdd(name: string) {
    try {
      const label = await createLabel.mutateAsync({ workspaceId, name });
      setNewLabel(null);
      await relabelAll(label, true);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'The label could not be created.',
      );
    }
  }

  const busy = progress !== null;

  /*
    Floating over the board's lower edge, not in the page flow above it. In the
    flow, the bar's arrival pushed every column down by its own height and its
    departure pulled them back — the board jumped under the pointer at exactly
    the moment someone was aiming at cards. Overlaid, nothing else moves.

    The bottom rather than the top because the top already holds the filter row
    and the phase headings, and because the hand that just selected cards is on
    the board; bottom-centre is where desktop apps that offer bulk actions put
    them (mail, files, design tools), so it is also where a reader looks. The
    wrapper takes no pointer events so the board either side of the bar stays
    clickable; the bar itself takes them back.

    Pinned to the window, not to the board's box (2026-10-05). Since the page
    scrolls rather than the board (2026-09-29), an absolute bar sat at the
    bottom of the whole tall page and scrolled away with it. The wrapper is
    sticky to the bottom of the scrolling `<main>` and has no height of its
    own, so it still moves nothing; `-mt-6` takes back the section's gap.
  */
  return (
    // On a phone (spec 262) the bar is pinned to the screen's bottom edge,
    // above the home indicator, where the thumb already is.
    <div className="pointer-events-none sticky bottom-6 z-30 -mt-6 h-0 max-md:fixed max-md:inset-x-3 max-md:bottom-[max(0.75rem,env(safe-area-inset-bottom))] max-md:mt-0">
      <div className="absolute inset-x-0 bottom-0 flex justify-center">
        <div
          role="toolbar"
          aria-label="Selection"
          className="border-border bg-popover shadow-floating pointer-events-auto flex max-w-full flex-wrap items-center gap-2 rounded-xl border px-3 py-2 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2"
        >
          <span className="text-foreground text-sm font-medium tabular-nums">
            {busy
              ? `${String(progress.done)} of ${String(progress.total)}`
              : `${String(count)} selected`}
          </span>

          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy || targets.length === 0}
                />
              }
            >
              <ArrowRightIcon aria-hidden="true" className="size-3.5" />
              Move to…
              <ChevronDownIcon aria-hidden="true" className="size-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {targets.map((target) => (
                <DropdownMenuItem
                  key={target.stepId}
                  onClick={() => {
                    void moveAll(target.stepId);
                  }}
                >
                  {target.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger
              render={<Button variant="outline" size="sm" disabled={busy} />}
            >
              <TagIcon aria-hidden="true" className="size-3.5" />
              Add label
              <ChevronDownIcon aria-hidden="true" className="size-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {labelRegistry.length === 0 ? (
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-muted-foreground font-normal">
                    No labels in this workspace yet
                  </DropdownMenuLabel>
                </DropdownMenuGroup>
              ) : (
                labelRegistry.map((label) => (
                  <DropdownMenuItem
                    key={label.id}
                    onClick={() => {
                      void relabelAll(label, true);
                    }}
                  >
                    {label.name}
                  </DropdownMenuItem>
                ))
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  setNewLabel('');
                }}
              >
                <PlusIcon aria-hidden="true" className="size-3.5" />
                New label…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {newLabel !== null ? (
            <form
              className="flex items-center gap-1.5"
              onSubmit={(event) => {
                event.preventDefault();
                const name = newLabel.trim();
                if (name.length > 0) void createAndAdd(name);
              }}
            >
              <Input
                aria-label="New label name"
                placeholder="Label name"
                autoFocus
                value={newLabel}
                onChange={(event) => {
                  setNewLabel(event.target.value);
                }}
                className="h-8 w-40"
              />
              <Button
                type="submit"
                size="sm"
                disabled={
                  busy || createLabel.isPending || newLabel.trim().length === 0
                }
              >
                Create and add
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setNewLabel(null);
                }}
              >
                Cancel
              </Button>
            </form>
          ) : null}

          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy || labelsOnSelection.length === 0}
                />
              }
            >
              <TagsIcon aria-hidden="true" className="size-3.5" />
              Remove label
              <ChevronDownIcon aria-hidden="true" className="size-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {labelsOnSelection.map((label) => (
                <DropdownMenuItem
                  key={label.id}
                  onClick={() => {
                    void relabelAll(label, false);
                  }}
                >
                  {label.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => {
              void hideAll();
            }}
          >
            <EyeOffIcon aria-hidden="true" className="size-3.5" />
            Hide
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            disabled={busy}
            onClick={selection.clear}
          >
            <XIcon aria-hidden="true" className="size-3.5" />
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
}
